// PRD-0191 Review visuals: the blog post's hero and social (OG) card next to the draft. Shows the
// proposals per slot, lets the operator pick one, regenerate (optionally with a note) or choose an
// image from the asset library. Only owned, licensed or approved-UGC images can be used.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Brief, MediaAsset, ReviewApprovalItem } from "../../../../shared/types";
import { isPublishableRights, type VisualSlot, type VisualSlotEntry } from "../../../../shared/visuals";
import { apiUrl } from "../../lib/api";
import { useMediaList } from "../../hooks/useMedia";
import { useBriefVisuals, useGenerateVisuals, useSelectVisual } from "../../hooks/useVisuals";
import { LoadErrorState } from "../ui/LoadState";

interface Props {
  brief: Brief | ReviewApprovalItem;
  brandId: string;
}

const SLOT_LABELS: Record<VisualSlot, { title: string; hint: string }> = {
  hero: { title: "Hero image", hint: "Shown at the top of the post. Text-free." },
  og: { title: "Social card", hint: "1200×630 preview for links shared on social and chat." },
};
const SOURCE_LABELS: Record<VisualSlotEntry["source"], string> = { reuse: "From library", generated: "Generated", rendered: "Card", manual: "Chosen" };
const LOCKED = new Set(["scheduled", "published", "cancelled"]);

/**
 * Image src: the signed Mech Storage URL from the list response. The API's /file route is only a
 * fallback (an <img> on helloconvo.ai cannot load API-origin bytes when CORP blocks them).
 */
export function thumb(brandId: string, assetId: string, signedUrl?: string | null) {
  return signedUrl || apiUrl(`/api/brands/${encodeURIComponent(brandId)}/media/${encodeURIComponent(assetId)}/file`);
}

function errorText(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function ReviewVisualsPanel({ brief, brandId }: Props) {
  const [pollUntil, setPollUntil] = useState(0);
  const visualsQ = useBriefVisuals(brandId, brief.id, pollUntil);
  const generate = useGenerateVisuals(brandId, brief.id);
  const select = useSelectVisual(brandId, brief.id, brief.campaign_id);
  const [note, setNote] = useState("");
  const [pickerSlot, setPickerSlot] = useState<VisualSlot | null>(null);
  const [message, setMessage] = useState<{ tone: "info" | "error"; text: string } | null>(null);
  const locked = LOCKED.has(brief.state);
  const approved = brief.state === "approved";

  async function handleGenerate(slots: VisualSlot[]) {
    setMessage(null);
    try {
      await generate.mutateAsync({ slots, ...(note.trim() ? { note: note.trim() } : {}), regenerate: true });
      setPollUntil(Date.now() + 4 * 60_000);
      setMessage({ tone: "info", text: "Generating… new options appear here in a minute or two." });
      setNote("");
    } catch (error) {
      setMessage({ tone: "error", text: errorText(error, "Could not start generating images") });
    }
  }

  async function handleSelect(slot: VisualSlot, assetId: string) {
    setMessage(null);
    try {
      const result = await select.mutateAsync({ slot, assetId });
      setPickerSlot(null);
      if (result.warnings?.length) setMessage({ tone: "error", text: "The hero changed, but the social card could not be re-rendered. Re-render the card before publishing." });
      else if (approved) setMessage({ tone: "info", text: "Image changed: the post went back to review. Approve it again before publishing." });
    } catch (error) {
      setMessage({ tone: "error", text: errorText(error, "Could not use that image") });
    }
  }

  if (visualsQ.isLoading) return <p className="text-xs text-slate-400" data-testid="review-visuals-loading">Loading images…</p>;
  if (visualsQ.isError || !visualsQ.data) {
    return <LoadErrorState compact title="Images could not be loaded" description="The post's images are unavailable right now." onRetry={() => void visualsQ.refetch()} />;
  }
  const visuals = visualsQ.data;
  const busy = generate.isPending || select.isPending;

  return (
    <section className="space-y-3 rounded-md border border-slate-200 bg-white p-3" data-testid="review-visuals" aria-label="Post images">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">Images</h3>
        {approved && <p className="text-xs text-amber-700">Changing an image sends this approved post back to review.</p>}
      </div>
      {!visuals.has_kit && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          Set up the brand&apos;s visual kit to generate images.{" "}
          <Link to={`/brand/${encodeURIComponent(brandId)}/settings/visuals`} className="font-medium underline">Brand → Visuals</Link>
        </p>
      )}
      {(["hero", "og"] as const).map((slot) => {
        const group = visuals.slots[slot];
        const options = group.proposals;
        return (
          <div key={slot} className="space-y-2" data-testid={`visual-slot-${slot}`}>
            <div>
              <p className="text-xs font-semibold text-slate-800">{SLOT_LABELS[slot].title}</p>
              <p className="text-xs text-slate-500">{SLOT_LABELS[slot].hint}</p>
            </div>
            {group.selected ? (
              <figure className="space-y-1">
                <img src={thumb(brandId, group.selected.asset_id, group.selected.asset?.url)} alt={group.selected.asset?.alt_text ?? `${SLOT_LABELS[slot].title} for this post`}
                  className="max-h-48 w-full rounded border border-emerald-300 object-cover" />
                <figcaption className="text-xs text-emerald-800">Selected · {SOURCE_LABELS[group.selected.source]}</figcaption>
                {group.selected.stale && (
                  <p role="alert" className="text-xs text-amber-800" data-testid="og-stale">
                    This card was made from a different hero image. Re-render it before approving or publishing.
                  </p>
                )}
              </figure>
            ) : (
              <p className="text-xs text-slate-500">No {slot === "og" ? "social card" : "hero image"} selected yet.</p>
            )}
            {options.length > 0 && (
              <ul className="grid grid-cols-2 gap-2">
                {options.map((entry) => {
                  const publishable = entry.asset?.publishable ?? false;
                  return (
                    <li key={entry.id} className="space-y-1">
                      <img src={thumb(brandId, entry.asset_id, entry.asset?.url)} alt={entry.asset?.alt_text ?? "Proposed image"} className="aspect-video w-full rounded border border-slate-200 object-cover" />
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[11px] text-slate-500">{SOURCE_LABELS[entry.source]}{entry.stale ? " · older hero" : ""}</span>
                        <button type="button" disabled={locked || busy || !publishable} onClick={() => void handleSelect(slot, entry.asset_id)}
                          title={publishable ? undefined : "Usage rights not cleared"}
                          className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                          Use this
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {!locked && (
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy || !visuals.has_kit} onClick={() => void handleGenerate([slot])}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                  {slot === "og" ? "Re-render card" : "Regenerate"}
                </button>
                <button type="button" disabled={busy} onClick={() => setPickerSlot(pickerSlot === slot ? null : slot)}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50">
                  {pickerSlot === slot ? "Close library" : "Choose from library"}
                </button>
              </div>
            )}
            {pickerSlot === slot && <LibraryPicker brandId={brandId} onPick={(assetId) => void handleSelect(slot, assetId)} disabled={busy} />}
          </div>
        );
      })}
      {!locked && visuals.has_kit && (
        <label className="block text-xs text-slate-700">
          Direction for new images (optional)
          <input value={note} onChange={(event) => setNote(event.target.value.slice(0, 500))} placeholder="e.g. warmer, show a terminal"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs" />
        </label>
      )}
      {message && <p role={message.tone === "error" ? "alert" : "status"} className={`text-xs ${message.tone === "error" ? "text-red-700" : "text-slate-600"}`}>{message.text}</p>}
    </section>
  );
}

function LibraryPicker({ brandId, onPick, disabled }: { brandId: string; onPick: (assetId: string) => void; disabled: boolean }) {
  const params = useMemo(() => new URLSearchParams({ limit: "24" }), []);
  const mediaQ = useMediaList(brandId, params);
  if (mediaQ.isLoading) return <p className="text-xs text-slate-400">Loading library…</p>;
  if (mediaQ.isError) return <p role="alert" className="text-xs text-red-700">The asset library could not be loaded.</p>;
  const images = (mediaQ.data?.data ?? []).filter((asset: MediaAsset) => asset.mime_type.startsWith("image/"));
  if (!images.length) return <p className="text-xs text-slate-500">No images in the library yet.</p>;
  return (
    <ul className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="visual-library-picker">
      {images.map((asset: MediaAsset) => {
        const ok = isPublishableRights(asset.usage_rights);
        return (
          <li key={asset.id}>
            <button type="button" disabled={disabled || !ok} onClick={() => onPick(asset.id)} title={ok ? asset.label ?? "Use this image" : `Usage rights: ${asset.usage_rights}`}
              className="block w-full rounded border border-slate-200 disabled:cursor-not-allowed disabled:opacity-40">
              <img src={thumb(brandId, asset.id, asset.thumbnail_url)} alt={asset.alt_text ?? asset.label ?? "Library image"} className="aspect-square w-full object-cover" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
